import type { Callback, Config, Dict, RequestOptions } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import { v4 as uuidv4 } from 'uuid';
import { Environments } from '@monorepo/client-types';
import {
  Application,
  CheckoutContextState,
  Device,
  EventTypes,
} from '@monorepo/types';
import { getClientDevice } from '../../utils';
import { trackEvent as trackTypedEvent } from '../analytics/events/track-event';
import type { HubPageName } from '../analytics/page-tracking/constants';
import {
  CheckoutEntryPath,
  CheckoutSource,
  MIXPANEL_STORAGE_KEYS,
  PAGE_VIEW_CONSTANTS,
} from './constants';
import { mockMixpanel } from './mixpanel-mock';
import type { MockMixpanelInterface } from './types';

// Type declaration for window.mixpanel
declare global {
  interface Window {
    mixpanel?: MockMixpanelInterface;
  }
}

let isMixpanelInitialized = false;

/**
 * Normalize email for consistent Mixpanel tracking
 * Prevents duplicate profiles from case/whitespace variations
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

// Helper to check if we're in a browser environment for Next.js SSR
const isBrowser = () => typeof window !== 'undefined';

// Helper to get screen type for analytics
const getScreenType = (): 'desktop' | 'mobile' => {
  const device = getClientDevice();
  return device === 'mobile' ? 'mobile' : 'desktop';
};

// Helper to get the active mixpanel instance (mock or real)
const getMixpanel = () => {
  if (!isBrowser()) {
    return mixpanel;
  }

  return window.mixpanel || mixpanel;
};

export const initMixpanel = ({
  token,
  env,
  apiHost,
}: {
  token?: string;
  env: Environments;
  apiHost?: string;
}) => {
  if (!isBrowser()) {
    return;
  }

  const isDev = env !== Environments.Production;

  // Use mock in development when no token is provided
  if (isDev && !token) {
    console.warn('⚠️  Using Mixpanel MOCK mode (no token configured)');
    window.mixpanel = mockMixpanel;
    isMixpanelInitialized = true;
    return;
  }

  if (!token) {
    console.log('[Mixpanel] token is missing');
    return;
  }

  if (isDev) {
    console.log('[mixpanel] isDev', isDev);
  }

  const options = {
    debug: isDev,
    // First-party proxy to bypass ad blockers
    // Events route through our domain instead of api.mixpanel.com
    // Staging/Dev use mppx-dev.the5ers.com (EU proxy), Production uses mppx.the5ers.com (US proxy)
    api_host: apiHost || 'https://mppx.the5ers.com',
    // Enables tracking across subdomains
    cross_subdomain_cookie: true,
    // Ensures cookies are sent over HTTPS
    secure_cookie: true,
    // Ensures data is stored in cookies (default behavior)
    persistence: 'cookie',
  };

  // @types/mixpanel-browser is not yet supporting the latest version.
  // Starting with SDK version 2.60.0, the Javascript SDK can autocapture events when you enable it.
  // @link: https://docs.mixpanel.com/docs/quickstart/capture-events/autocapture
  mixpanel.init(token, options as unknown as Config);

  isMixpanelInitialized = true;
};

export const identifyMixpanel = ({
  uniqueId,
  properties = {},
  callback,
}: {
  uniqueId?: string;
  properties?: Dict;
  callback?: Callback;
}) => {
  if (!isBrowser() || !isMixpanelInitialized || !uniqueId) {
    return;
  }

  const mp = getMixpanel();
  mp.identify(uniqueId);

  // Normalize email if provided
  const normalizedProperties = { ...properties };
  if (normalizedProperties['$email']) {
    normalizedProperties['$email'] = normalizeEmail(
      normalizedProperties['$email'] as string,
    );
  }

  // Single batched call with device type and normalized properties
  const device = getClientDevice();
  const allProperties = {
    $device_type: device,
    ...normalizedProperties,
  };

  mp.people.set(allProperties, callback);
};

/**
 * Create alias linking old identity to new identity
 * CRITICAL: Call ONLY during signup (email or SSO)
 * Never call during login - only at account creation
 *
 * @param email - User's email (will be normalized)
 * @param userId - User's UUID from Cognito
 */
export const aliasMixpanel = ({
  email,
  userId,
}: {
  email: string;
  userId: string;
}) => {
  if (!isBrowser() || !isMixpanelInitialized || !email || !userId) {
    return;
  }

  const mp = getMixpanel();
  const normalizedEmail = normalizeEmail(email);

  // Create one-way link: normalizedEmail → userId
  // This allows pre-signup events (tracked with email) to be linked to post-signup events (tracked with UUID)
  mp.alias(userId, normalizedEmail);
};

/**
 * Reset Mixpanel identity on logout
 * Clears current user identity and generates new anonymous ID
 * Prevents post-logout events from being attributed to logged-out user
 */
export const resetMixpanel = () => {
  if (!isBrowser() || !isMixpanelInitialized) {
    return;
  }

  const mp = getMixpanel();
  mp.reset();
};

export const trackMixpanel = ({
  event,
  properties,
  callback,
  optionsOrCallback,
}: {
  event: EventTypes;
  properties?: Dict;
  optionsOrCallback?: RequestOptions | Callback;
  callback?: Callback;
}) => {
  if (!isBrowser() || !isMixpanelInitialized) {
    return;
  }

  const mp = getMixpanel();
  const device = getScreenType();

  mp.track(event, { ...properties, device }, optionsOrCallback, callback);
};

/**
 * Properties for standardized page view tracking
 */
export interface TrackPageViewParams {
  /** Standardized page name with 'hub_' prefix */
  page_name: HubPageName;
  /** User's current language setting (e.g., 'en', 'es', 'ja') */
  hub_language: string;
  /** Full URL of the page (auto-populated if not provided) */
  url?: string;
}

/**
 * Page view tracking with URL-based deduplication
 * Prevents duplicate page_view events within 500ms window
 *
 * Uses type-safe trackEvent internally for compile-time validation
 * while maintaining backward compatibility with existing API
 *
 * @param properties - Page view properties with standardized page_name and hub_language
 */
let lastTrackedUrl = '';
let lastTrackTime = 0;

export const trackPageView = (properties: TrackPageViewParams) => {
  if (!isBrowser()) {
    return;
  }

  const currentUrl = properties.url || window.location.href;
  const now = Date.now();

  const isDuplicate =
    currentUrl === lastTrackedUrl &&
    now - lastTrackTime < PAGE_VIEW_CONSTANTS.DEBOUNCE_MS;

  // Prevent same-URL re-fires within debounce window
  if (isMixpanelInitialized && isDuplicate) {
    return;
  }

  if (isMixpanelInitialized) {
    lastTrackedUrl = currentUrl;
    lastTrackTime = now;
  }

  const eventProperties = {
    page_name: properties.page_name,
    hub_language: properties.hub_language,
  };

  trackTypedEvent(EventTypes.PAGE_VIEW, eventProperties);
};

export const resetPageViewState = () => {
  lastTrackedUrl = '';
  lastTrackTime = 0;
};

/**
 * Set Mixpanel profile properties once (never overwrite)
 * Used for attribution properties like $initial_referrer
 */
export const setOnceMixpanel = ({
  uniqueId,
  properties = {},
  callback,
}: {
  uniqueId?: string;
  properties?: Dict;
  callback?: Callback;
}) => {
  if (isMixpanelInitialized && uniqueId) {
    const mp = getMixpanel();
    mp.people.set_once(properties, callback);
  }
};

/**
 * Registers super properties that will be sent with every Mixpanel event.
 * Super properties persist in cookies across sessions.
 * UTM properties are also stored in localStorage for the HTTP interceptor
 * (which runs before Mixpanel is initialized).
 */
export const registerMixpanelSuperProperties = (properties: {
  brand?: Application;
  device?: Device;
  hub_language?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}) => {
  // Persist UTMs to localStorage so the HTTP interceptor can read them
  // even before Mixpanel is initialized
  if (isBrowser()) {
    if (properties.utm_source) {
      localStorage.setItem('mp_utm_source', properties.utm_source);
    }
    if (properties.utm_medium) {
      localStorage.setItem('mp_utm_medium', properties.utm_medium);
    }
    if (properties.utm_campaign) {
      localStorage.setItem('mp_utm_campaign', properties.utm_campaign);
    }
  }

  if (!isBrowser() || !isMixpanelInitialized) {
    return;
  }

  const mp = getMixpanel();
  mp.register(properties);
};

/**
 * Extract UTM parameters from the current URL.
 * Returns only the UTM keys that are present — omitted keys won't overwrite existing super properties.
 */
export const getUtmParamsFromUrl = (): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
} => {
  if (!isBrowser()) {
    return {};
  }

  const url = new URL(window.location.href);
  const utmSource = url.searchParams.get('utm_source');
  const utmMedium = url.searchParams.get('utm_medium');
  const utmCampaign = url.searchParams.get('utm_campaign');

  return {
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
  };
};

/**
 * Read UTM super properties from the Mixpanel cookie.
 * Used by the HTTP interceptor to forward UTMs to backend events.
 */
export const getUtmSuperProperties = (): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
} => {
  if (!isBrowser()) {
    return {};
  }

  const utmSource = localStorage.getItem('mp_utm_source');
  const utmMedium = localStorage.getItem('mp_utm_medium');
  const utmCampaign = localStorage.getItem('mp_utm_campaign');

  return {
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
  };
};

export const setAuthMethod = (method: string) => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.setItem(MIXPANEL_STORAGE_KEYS.AUTH_METHOD, method);
};

export const getAuthMethod = (): string | null => {
  if (!isBrowser()) {
    return null;
  }
  return sessionStorage.getItem(MIXPANEL_STORAGE_KEYS.AUTH_METHOD);
};

export const clearAuthMethod = () => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.removeItem(MIXPANEL_STORAGE_KEYS.AUTH_METHOD);
};

const generateCheckoutSessionId = (): string => {
  return uuidv4();
};

export const setCheckoutEntryPath = (path: CheckoutEntryPath): void => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.setItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_ENTRY_PATH, path);
};

export const getCheckoutEntryPath = (): CheckoutEntryPath | null => {
  if (!isBrowser()) {
    return null;
  }
  const path = sessionStorage.getItem(
    MIXPANEL_STORAGE_KEYS.CHECKOUT_ENTRY_PATH,
  );
  if (
    path &&
    Object.values(CheckoutEntryPath).includes(path as CheckoutEntryPath)
  ) {
    return path as CheckoutEntryPath;
  }
  return null;
};

export const clearCheckoutEntryPath = (): void => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.removeItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_ENTRY_PATH);
};

export const isResumedSession = (): boolean => {
  const path = getCheckoutEntryPath();
  return (
    path === CheckoutEntryPath.ResumeExplicit ||
    path === CheckoutEntryPath.ResumeImplicit
  );
};

export const setCheckoutSessionId = (sessionId?: string): string => {
  const id = sessionId || generateCheckoutSessionId();
  if (!isBrowser()) {
    return id;
  }
  sessionStorage.setItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SESSION_ID, id);
  return id;
};

export const getCheckoutSessionId = (): string | null => {
  if (!isBrowser()) {
    return null;
  }
  return sessionStorage.getItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SESSION_ID);
};

export const clearCheckoutSessionId = (): void => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.removeItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SESSION_ID);
};

export const setCheckoutSource = (source: CheckoutSource): void => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.setItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SOURCE, source);
};

export const getCheckoutSource = (): CheckoutSource | null => {
  if (!isBrowser()) {
    return null;
  }
  const source = sessionStorage.getItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SOURCE);
  if (Object.values(CheckoutSource).includes(source as CheckoutSource)) {
    return source as CheckoutSource;
  }
  return null;
};

export const clearCheckoutSource = (): void => {
  if (!isBrowser()) {
    return;
  }
  sessionStorage.removeItem(MIXPANEL_STORAGE_KEYS.CHECKOUT_SOURCE);
};

export const getCheckoutTrackingProperties = (): {
  checkoutEntryPath: CheckoutEntryPath | null;
  isResumedSession: boolean;
  checkoutSessionId: string | null;
  checkoutSource: CheckoutSource | null;
} => {
  const path = getCheckoutEntryPath();
  return {
    checkoutEntryPath: path,
    isResumedSession:
      path === CheckoutEntryPath.ResumeExplicit ||
      path === CheckoutEntryPath.ResumeImplicit,
    checkoutSessionId: getCheckoutSessionId(),
    checkoutSource: getCheckoutSource(),
  };
};

/**
 * Determines the checkout context state based on cart contents and session state
 *
 * @param cartItemCount - Number of items in the cart
 * @param isTerminating - Whether the cart is being emptied (for delete cart event only)
 * @returns CheckoutContextState - 'active', 'terminated', or 'none'
 */
export const getCheckoutContextState = (
  cartItemCount: number,
  isTerminating = false,
): CheckoutContextState => {
  if (isTerminating) {
    return CheckoutContextState.Terminated;
  }

  const sessionId = getCheckoutSessionId();
  if (!sessionId) {
    return CheckoutContextState.None;
  }

  return cartItemCount > 0
    ? CheckoutContextState.Active
    : CheckoutContextState.None;
};

export const clearAllCheckoutTracking = (): void => {
  clearCheckoutEntryPath();
  clearCheckoutSessionId();
  clearCheckoutSource();
};

export const initCheckoutSession = (
  entryPath: CheckoutEntryPath,
  source?: CheckoutSource,
): string => {
  setCheckoutEntryPath(entryPath);
  const sessionId = setCheckoutSessionId();
  if (source) {
    setCheckoutSource(source);
  } else {
    clearCheckoutSource();
  }
  return sessionId;
};

/*
  Reuses the existing checkout session if one exists, otherwise creates a new one.
  When reusing, only updates entryPath and source if they are not already set,
  preserving the original entry context.
*/
export const getOrInitCheckoutSession = (
  entryPath: CheckoutEntryPath,
  source?: CheckoutSource,
): string => {
  const existingSessionId = getCheckoutSessionId();
  if (existingSessionId) {
    if (!getCheckoutEntryPath()) {
      setCheckoutEntryPath(entryPath);
    }
    if (source && !getCheckoutSource()) {
      setCheckoutSource(source);
    }
    return existingSessionId;
  }
  return initCheckoutSession(entryPath, source);
};
